import {defineStore} from "pinia";
import {nextTick, ref} from "vue";
import {useAuthStore} from "@/stores/auth";
import {useApiStore} from "@/stores/api";

interface EmpathicWidgetConfig {
    apiKey: string;
    widgetId: string;
    apiUrl?: string;
    position?: string;
    enabled?: boolean;
    external_id?: string | number;
    handle_submit?: ((payload: any) => Promise<any>) | string;
}

interface EmpathicTicketData {
    shipmentId?: number;
    clientReceptionNumber?: string;
    wmsReceptionNumber?: number;
    eta?: string | null;
    creationDate?: string | null;
    status?: string;
    packsCount?: number;
    bankNo?: string;
    bankName?: string;
    iban?: string;
    userName?: string;
    userEmail?: string;
    notificationDate?: string;

    [key: string]: any;
}

interface FieldMapping {
    field: string;
    value: any;
}

declare global {
    interface Window {
        EMPATHIC_CONFIG?: EmpathicWidgetConfig;
        EmpathicWidget?: {
            init: (config: EmpathicWidgetConfig) => void;
            open: () => void;
            close: () => void;
            destroy: () => void;
            toggle: () => void;
            setContext?: (data: EmpathicTicketData) => void;
            mapFields?: (fields: Record<string, FieldMapping>) => void;
        };
    }
}

const initialized = ref(false);

export enum WidgetType {
    HELP = "c59d891c-439d-4041-8b03-68fc3b9b5608"
}

export const useEmpathicWidgetStore = defineStore("empathicWidget", () => {
    const authStore = useAuthStore();
    const api = useApiStore();
    const API_KEY = "etk_2Cm9AxnmoYI1Yi3t2sw8Xn4t6m3EoAdL";
    const API_URL = "https://nopgjscfmfjtxcihdbwg.supabase.co/functions/v1";
    const BASE_URL = import.meta.env.VITE_BASE_URL;

    const handleSubmit = async (payload: any) => {
        console.log("Handling submit with payload:", payload);

        const response = await api.axiosApi.post('submitTicket', payload);

        console.log("API response:", response);
        return response.data;
    };

    const currentWidgetType = ref<WidgetType | null>(null);

    const initializeWidget = async (widgetType: WidgetType) => {
        currentWidgetType.value = widgetType;

        if (initialized.value === true) {
            resetWidget();
        }
        if (typeof window.EmpathicWidget !== "undefined" && typeof window.EmpathicWidget.init === "function") {
            try {
                await window.EmpathicWidget.init({
                    widgetId: widgetType,
                    apiKey: API_KEY,
                    apiUrl: API_URL,
                    position: "bottom-right",
                    enabled: false,
                    external_id: authStore.user.id,
                    handle_submit: handleSubmit,
                });
                initialized.value = true;
            } catch (e) {
                console.error("Error initializing widget:", e);
            }
        } else {
            console.warn("EmpathicWidget not loaded yet. Set global config.");
            window.EMPATHIC_CONFIG = {
                widgetId: widgetType,
                apiKey: API_KEY,
                apiUrl: API_URL,
                position: "bottom-right",
                enabled: false,
                external_id: authStore.user.id,
                handle_submit: handleSubmit,
            };
            initialized.value = true;
        }
    };

    const openWidget = async (widgetType: WidgetType, fields?: Record<string, FieldMapping>) => {
        if (typeof window.EmpathicWidget === "undefined") {
            console.error("EmpathicWidget not loaded yet");
            return;
        }

        if (currentWidgetType.value == null) {
            await initializeWidget(widgetType);
        } else if (currentWidgetType.value !== widgetType) {
            closeWidget();
            resetWidget();
            await initializeWidget(widgetType);
        }

        try {
            window.EmpathicWidget.open();
            if (fields && typeof window.EmpathicWidget.mapFields === "function") {
                setTimeout(() => {
                    window.EmpathicWidget!.mapFields!(fields);
                }, 100);
            }
        } catch (e) {
            console.error("Error opening widget:", e);
        }
    };
    const closeWidget = () => {
        if (typeof window.EmpathicWidget !== "undefined") {
            try {
                window.EmpathicWidget.close();
                // console.log("Closed widget:");
                // resetWidget();
            } catch (e) {
                console.error("Error closing widget:", e);
            }
        }
    };
    const resetWidget = () => {

        if (initialized.value === false) {
            return;
        }

        initialized.value = false;
        // console.log("Reset widget:");
        window.EmpathicWidget.destroy();
        currentWidgetType.value = null;
    };
    const getCurrentWidgetType = () => currentWidgetType.value;

    return {
        currentWidgetType,
        initializeWidget,
        openWidget,
        closeWidget,
        resetWidget,
        getCurrentWidgetType,
    };
});
